import { useSelector } from '@common/performance/useSelector';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { CaretDownIcon, CaretUpIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack, StackItem, Tree, Columns, DataNode, Button, Editor } from '@prophecy/ui-v3';
import { GraphProvider } from '@prophecy/ui-v3/Graph/GraphContext';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Header } from '../Header';
import { SubHeader } from '../SubHeader';
import { LineageColumns, LineageState, Selector, ViewType } from '../types';
import { Canvas } from './Canvas';
import { useProcessSelect } from './hook';

function prepareTreeData(columns: LineageColumns[]) {
  const data: DataNode[] = [];
  columns.forEach((column) => {
    const columnData = {
      title: column.columnName,
      key: column.columnName,
      selectable: false,
      children: column.components.map((component) => ({
        title: component.name,
        key: component.id
      }))
    };
    data.push(columnData);
  });
  return data;
}

const StyledHeader = styled(Stack)`
  background: ${theme.colors.gray100};
  color: ${theme.colors.gray900};
  font-weight: ${theme.fontWeight.semiBold};
  padding: ${theme.spaces.x6} ${theme.spaces.x12};
  border: 1px solid ${theme.colors.gray200};
`;
const StyledColumn = styled(Columns.Column)`
  border: 1px solid ${theme.colors.gray200};
`;
const StyledStackItem = styled(StackItem)`
  text-align: center;
`;

const COLUMNS_TREE_HEIGHT = 160;

export function PipelineView() {
  const codeView = useSelector<LineageState, LineageState['metadata']['codeView']>((state) => state.metadata.codeView);
  const graphPath = useSelector<LineageState, string>((state) => state.graphPath);

  const selectedComponent = useSelector<LineageState, Selector['selectedComponent']>(
    (state) => state.metadata?.selectors?.selectedComponent
  );
  const onProcessSelect = useProcessSelect(graphPath);
  const isCode = codeView && selectedComponent;
  const [isCodeView, setCodeView] = useState(Boolean(isCode));

  useEffect(() => {
    setCodeView(Boolean(isCode));
  }, [isCode]);
  return (
    <>
      <StackItem grow='1'>
        <Stack height='100%' width='100%'>
          <Header viewType={ViewType.pipeline} />
          <SubHeader />
          <GraphProvider key={graphPath}>
            <Canvas />
          </GraphProvider>
        </Stack>
      </StackItem>
      {isCode ? (
        <Stack height={isCodeView ? '250px' : '35px'}>
          <StyledHeader direction='horizontal' align='end'>
            <StyledStackItem grow='1'>
              {selectedComponent?.label}: {selectedComponent?.type}
            </StyledStackItem>
            <Button
              variant='secondaryGray'
              size='xs'
              icon={isCodeView ? <CaretDownIcon type='extended' /> : <CaretUpIcon type='extended' />}
              onClick={() => setCodeView(!isCodeView)}
              aria-label={isCodeView ? 'Collapse code view' : 'Expand code view'}
              data-test-id='lineage-pipeline-view-toggle-code-view-button'
            />
          </StyledHeader>
          <Columns height='216px'>
            <StyledColumn width='250px'>
              <StyledHeader>Previous Columns</StyledHeader>
              <Tree
                selectable
                height={COLUMNS_TREE_HEIGHT}
                onSelect={(_selectedKeys, { node }) => onProcessSelect(node.key as string)}
                treeData={prepareTreeData(codeView?.previousColumns || [])}
              />
            </StyledColumn>
            <Columns.Column width='1fr'>
              <Editor
                height='100%'
                value={codeView?.code}
                language={codeView?.language as BackEndCodeLanguage}
                readOnly={true}
              />
            </Columns.Column>
            <StyledColumn width='250px'>
              <StyledHeader>Next Columns</StyledHeader>
              <Tree
                selectable
                height={COLUMNS_TREE_HEIGHT}
                onSelect={(_selectedKeys, { node }) => onProcessSelect(node.key as string)}
                treeData={prepareTreeData(codeView?.nextColumns || [])}
              />
            </StyledColumn>
          </Columns>
        </Stack>
      ) : null}
    </>
  );
}
